"use client";

import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Cpu, Wrench, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, CircleQuestionMark as HelpCircle, Truck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { EcommerceProductsProvider, useEcommerceProducts, ProductPrice, type EcommerceProduct } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  product_cat: 18,
  per_page: 9,
  order: "asc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
const GROUP_BY_PRODUCT_NAME: Record<string, 'motor' | 'console' | 'safety' | 'other'> = {
  'Mechanical Bull Control Box': 'console',
  'Mechanical Bull Safety & Operator Upgrade Kit': 'console',
  'Mechanical Bull Motor & Drive Parts': 'motor',
  'Inflatable Maintenance & Protection Kit': 'safety',
  'Mechanical Bull Safety Donut & Shaft Pad': 'safety',
  'Mechanical Bull Air Blower': 'safety',
  'Mechanical Bull Replacement Horns': 'other',
  'Mechanical Bull Replacement Saddle & Riding Gear': 'other',
  'Mechanical Bull Transport Cart': 'other'
};
const COMPONENT_TYPE_BY_NAME: Record<string, string> = {
  'Mechanical Bull Control Box': 'Consoles & Controls',
  'Mechanical Bull Safety & Operator Upgrade Kit': 'Consoles & Controls',
  'Mechanical Bull Motor & Drive Parts': 'Motors & Drive',
  'Inflatable Maintenance & Protection Kit': 'Pads & Safety',
  'Mechanical Bull Safety Donut & Shaft Pad': 'Pads & Safety',
  'Mechanical Bull Air Blower': 'Parts & Accessories',
  'Mechanical Bull Replacement Horns': 'Parts & Accessories',
  'Mechanical Bull Replacement Saddle & Riding Gear': 'Parts & Accessories',
  'Mechanical Bull Transport Cart': 'Parts & Accessories'
};
function CatalogConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = useEcommerceProducts();
  const [activeFilter, setActiveFilter] = useState("all");
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(idx => <div key={idx} data-index={idx} className="border border-border bg-card p-6 rounded-none animate-pulse space-y-4">
            <div className="aspect-square bg-muted rounded-none" />
            <div className="h-4 w-24 bg-muted rounded-none" />
            <div className="h-6 w-3/4 bg-muted rounded-none" />
            <div className="h-4 w-full bg-muted rounded-none" />
            <div className="h-8 w-1/3 bg-muted rounded-none" />
            <div className="h-10 w-full bg-muted rounded-none" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-destructive/40 bg-card p-8 text-center rounded-none my-8">
        <p className="text-base text-destructive font-medium">
          Unable to load parts catalog specifications at this time.
        </p>
        <Button onClick={() => refetch()} variant="outline" className="mt-4 rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background font-serif uppercase tracking-wider">
          Retry Connection
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="border border-border bg-card p-12 text-center rounded-none my-8">
        <Cpu className="mx-auto size-10 text-muted-foreground stroke-[1.5]" />
        <h3 className="mt-4 text-xl font-serif font-bold uppercase text-foreground">
          No parts currently indexed
        </h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
          All commercial assemblies are undergoing inventory audit. Contact our technical team for custom parts procurement.
        </p>
        <Button asChild className="mt-6 bg-primary text-primary-foreground rounded-none font-serif uppercase tracking-wider">
          <Link to="/ContactPage">Contact Technical Support</Link>
        </Button>
      </div>;
  }
  const filteredProducts = products.filter((product: EcommerceProduct) => {
    if (activeFilter === "all") return true;
    const group = GROUP_BY_PRODUCT_NAME[product.title] ?? "other";
    return group === activeFilter;
  });
  return <div className="space-y-10">
      {/* Category Filter Pills (Internal Visual Tabs) */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
        <div className="flex flex-wrap gap-2">
          <Button variant={activeFilter === "all" ? "default" : "outline"} size="sm" onClick={() => setActiveFilter("all")} className={`rounded-none font-mono text-xs uppercase tracking-wider ${activeFilter === "all" ? "bg-secondary text-secondary-foreground" : "border-border text-foreground hover:bg-muted"}`}>
            All Components ({products.length})
          </Button>
          <Button variant={activeFilter === "motor" ? "default" : "outline"} size="sm" onClick={() => setActiveFilter("motor")} className={`rounded-none font-mono text-xs uppercase tracking-wider ${activeFilter === "motor" ? "bg-secondary text-secondary-foreground" : "border-border text-foreground hover:bg-muted"}`}>
            Motors & Drive
          </Button>
          <Button variant={activeFilter === "console" ? "default" : "outline"} size="sm" onClick={() => setActiveFilter("console")} className={`rounded-none font-mono text-xs uppercase tracking-wider ${activeFilter === "console" ? "bg-secondary text-secondary-foreground" : "border-border text-foreground hover:bg-muted"}`}>
            Consoles & Controls
          </Button>
          <Button variant={activeFilter === "safety" ? "default" : "outline"} size="sm" onClick={() => setActiveFilter("safety")} className={`rounded-none font-mono text-xs uppercase tracking-wider ${activeFilter === "safety" ? "bg-secondary text-secondary-foreground" : "border-border text-foreground hover:bg-muted"}`}>
            Pads & Safety
          </Button>
        </div>

        <div className="text-xs font-mono text-muted-foreground uppercase">
          Displaying {filteredProducts.length} units
        </div>
      </div>

      {/* 3-Column Industrial Parts Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredProducts.map((product: EcommerceProduct, idx: number) => {
        const skuCode = product.customFields?.sku || `MBS-${product.id.toString().padStart(4, "0")}`;
        const compatibilityText = product.customFields?.compatibility || product.short_description?.replace(/<[^>]+>/g, "").trim() || "Confirm compatibility with your machine before ordering.";
        const componentTypeLabel = COMPONENT_TYPE_BY_NAME[product.title] ?? "Parts & Accessories";
        return <Card key={product.id} data-index={idx} className="group flex flex-col justify-between rounded-none border border-border bg-card p-0 transition-[border-color] duration-200 hover:border-foreground">
              <div>
                {/* 1:1 Macro Component Image Window */}
                <div className="relative aspect-square w-full overflow-hidden border-b border-border bg-muted p-4">
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" className="h-full w-full object-contain object-center grayscale-[20%] transition-transform duration-300 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                      <Wrench className="size-16 stroke-[1.25] text-muted-foreground/60" />
                    </div>}
                </div>

                {/* Card Content & Spec Plate */}
                <CardHeader className="p-5 pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                      SKU: {skuCode}
                    </span>
                    <span className="inline-flex items-center gap-1 font-mono text-[11px] text-foreground font-medium">
                      <CheckCircle2 className="size-3 text-primary" /> In Stock
                    </span>
                  </div>

                  {/* Component Category Type Badge */}
                  <div className="mt-1">
                    <span className="inline-block border border-border bg-muted px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {componentTypeLabel}
                    </span>
                  </div>

                  <CardTitle className="mt-2 text-xl font-serif font-bold uppercase tracking-tight text-foreground leading-snug">
                    <Link to={product.link || `/ContactPage?sku=${skuCode}`} className="hover:text-primary transition-colors">
                      {product.title}
                    </Link>
                  </CardTitle>
                </CardHeader>

                <CardContent className="px-5 pb-4 space-y-3">
                  {/* Technical Compatibility Summary Line */}
                  <div className="border-t border-border/80 pt-3">
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                      <span className="font-mono uppercase font-semibold text-foreground/80">
                        Compatibility:{" "}
                      </span>
                      {compatibilityText}
                    </p>
                  </div>

                  {/* Unit Pricing Container */}
                  <div className="flex items-baseline justify-between pt-1 border-t border-dashed border-border/60">
                    <span className="font-mono text-xs uppercase text-muted-foreground">
                      Base Spec Unit Price
                    </span>
                    <div className="text-lg font-serif font-bold text-foreground">
                      <ProductPrice product={product} />
                    </div>
                  </div>
                </CardContent>
              </div>

              {/* Action Buttons */}
              <CardFooter className="p-5 pt-0 flex flex-col sm:flex-row gap-2 w-full">
                <Button asChild variant="outline" className="w-full sm:w-auto sm:flex-1 rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background font-serif uppercase tracking-wider font-semibold text-xs py-2.5 transition-colors cursor-pointer">
                  <Link to={product.link || "#"}>
                    View Details
                  </Link>
                </Button>
                <Button asChild className="w-full sm:w-auto sm:flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-serif uppercase tracking-wider font-semibold text-xs py-2.5 transition-colors cursor-pointer">
                  <Link to="/RequestQuotePage">
                    Enquire About This Product
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-6 mt-10">
          <div className="font-mono text-xs text-muted-foreground uppercase">
            Page {currentPage} of {totalPages}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="rounded-none border-border text-foreground hover:bg-muted font-mono text-xs uppercase tracking-wider">
              <ChevronLeft className="mr-1 size-3.5" />
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="rounded-none border-border text-foreground hover:bg-muted font-mono text-xs uppercase tracking-wider">
              Next
              <ChevronRight className="ml-1 size-3.5" />
            </Button>
          </div>
        </div>}
    </div>;
}
export default function Catalog() {
  return <section id="product-catalog" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="mb-12 border-b border-border pb-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-3xl space-y-4">
              <div className="inline-flex items-center gap-2 border border-border bg-muted px-3 py-1 font-mono text-xs uppercase tracking-widest text-muted-foreground">
                <Wrench className="size-3.5 text-primary" />
                CURRENT PARTS INVENTORY
              </div>
              <h2 className="text-3xl md:text-5xl font-serif font-bold uppercase tracking-tight text-foreground">
                Shop Mechanical Bull Parts
              </h2>
              <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Start with the parts currently available. Browse the catalog below to compare price, availability, part type and any model-specific information supplied for each item. Open the individual product page before enquiring so you can review the available compatibility details and confirm exactly what comes with the part.
              </p>
              <p className="text-sm md:text-base text-muted-foreground max-w-2xl leading-relaxed">
                Mechanical bull replacement parts are not automatically interchangeable. Even when two components look similar, the connection type, dimensions, electrical requirements or controller generation may differ.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button asChild variant="outline" className="rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background font-serif uppercase tracking-wider">
                <Link to="/ContactPage">Request Custom Parts Quote</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Dynamic WooCommerce Products Catalog Consumer */}
        <EcommerceProductsProvider className="products-grid-wrapper" wp_query={{
        product_cat: [18],
        per_page: 9,
        order: "asc",
        orderby: "date"
      }}>
          <CatalogConsumer />
        </EcommerceProductsProvider>

        {/* Technical Guidance Footer Bar */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-border pt-10">
          <div className="flex items-start gap-4">
            <div className="p-2 border border-border bg-muted text-foreground">
              <AlertCircle className="size-5 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-serif font-semibold uppercase text-foreground">
                Confirm Compatibility Before Ordering
              </h3>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                Review machine specifications, connector types, and physical dimensions before placing an order to verify component match.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-2 border border-border bg-muted text-foreground">
              <HelpCircle className="size-5 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-serif font-semibold uppercase text-foreground">
                Contact Support With Photos & Model Info
              </h3>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                Need verification? Send photos of your serial plate, existing assembly, and wiring harness to our technical support team.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-2 border border-border bg-muted text-foreground">
              <Truck className="size-5 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-serif font-semibold uppercase text-foreground">
                Check Shipping Info Page
              </h3>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                Consult our{" "}
                <Link to="/ShippingWarrantyAndOrderingPage" className="text-foreground underline underline-offset-4 hover:text-primary font-medium">
                  Shipping Info page
                </Link>{" "}
                for lead times, freight handling details, and transit policies.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}